import React, { useState, useEffect } from "react";
import { useAppDispatch, useAppSelector } from "../../store/hook";
import { fetchProgramList, Program } from "../../features/program/ProgramSlice";
import { RootState } from "../../store/store";
import { Loader2, AlertCircle, ArrowLeft, Plus, Trash2, Calendar as CalendarIcon } from "lucide-react";
import ReviewPage from "./ReviewPage";
import { DateRangePicker } from "react-date-range";
import CustomModal from "../common/CustomModal";

/* --------------------------------- Interfaces ------------------------------ */

interface BuildCataloguePageProps {
  currency: string;
  country: string;
  paymentType: string;
  name: string;
  businessName: string;
  hostname: string;
  onBack: () => void;
  onBackListing: () => void;
  password: string;
  email: string;
  contactInformation: {
    email: string;
    phone: string;
    contactPerson: string;
    address: {
      city: string;
      state: string;
      street: string;
      country: string;
      zipCode: string;
    };
  };
  isPurchaseOrderRequired: boolean;
  allowPurchaseOrderCreation: boolean;
}

interface DiscountRange {
  min_order_value: number;
  max_order_value: number | null;
  discount: number | null;
  discount_error: string;
  max_order_value_error: string;
}

export interface ProgramForm {
  [key: string]: {
    checked: boolean;
    discount_ranges: DiscountRange[];
    program_details: Program;
    contractStartDate: string;
    contractEndDate: string;
    contractError: string;
    // Whether payment gateway should be enabled for this program (used in edit flow)
    enablePaymentGateway?: boolean;
  };
}

interface DateModalState {
  programId: string;
}

const BuildCataloguePage: React.FC<BuildCataloguePageProps> = ({ onBack, onBackListing, ...props }) => {

  /* --------------------------------- Redux ------------------------------ */

  const dispatch = useAppDispatch();
  const { programError, programLoading, programList } = useAppSelector((state: RootState) => state.program);

  /* --------------------------------- States ------------------------------ */

  const [showReview, setShowReview] = useState(false);
  const [program, setProgram] = useState<ProgramForm>({});
  const localFormData = { ...props };
  
  // Date range modal state
  const [dateModalState, setDateModalState] = useState<DateModalState | null>(null);

  /* --------------------------------- Hooks ------------------------------ */

  // Helper function to format date in YYYY-MM-DD format
  const formatDateToLocalString = (date: Date) => {
    const year = date.getFullYear()
    const month = String(date.getMonth() + 1).padStart(2, '0')
    const day = String(date.getDate()).padStart(2, '0')
    return `${year}-${month}-${day}`
  }

  // Helper function to format date range for display
  const formatDateRange = (startDate: string, endDate: string) => {
    if (!startDate || !endDate) return "Select date range"
    
    const start = new Date(startDate)
    const end = new Date(endDate)
    
    const startYear = start.getFullYear()
    const endYear = end.getFullYear()
    
    if (startYear === endYear) {
      return `${start.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })} - ${end.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}`
    } else {
      return `${start.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })} - ${end.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}`
    }
  }

  useEffect(() => {
    dispatch(fetchProgramList({ limit: 1000 }));
  }, [dispatch]);

  /* ------------------------------ Event Handlers ---------------------------- */

  const scrollToFirstError = (programId: string, rangeIndex: number) => {
    setTimeout(() => {
      const errorElement = document.querySelector(`[data-program-id="${programId}"][data-range-index="${rangeIndex}"]`);
      if (errorElement) {
        errorElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
    }, 100);
  };

  const validateAllRanges = (): { isValid: boolean; firstError?: { programId: string; rangeIndex: number } } => {
    for (const [programId, programData] of Object.entries(program)) {
      if (!programData.checked) continue;
      
      // Check if contract dates are required
      if (!programData.contractStartDate || !programData.contractEndDate) {
        return { isValid: false, firstError: { programId, rangeIndex: 0 } };
      }
      
      for (let i = 0; i < programData.discount_ranges.length; i++) {
        const range = programData.discount_ranges[i];

       
        
        // Check if discount is required
        if (!range.discount) {
          return { isValid: false, firstError: { programId, rangeIndex: i } };
        }
        
        // Check if max value is required
        if (!range.max_order_value && i !== programData.discount_ranges.length - 1) {
          return { isValid: false, firstError: { programId, rangeIndex: i } };
        }
        
        // Check if max value is greater than previous max value
        if (i > 0) {
          const previousRange = programData.discount_ranges[i - 1];
          if (range.max_order_value && previousRange.max_order_value && 
              range.max_order_value <= previousRange.max_order_value) {
            return { isValid: false, firstError: { programId, rangeIndex: i } };
          }
        }
      }
    }
    
    return { isValid: true };
  };

  const validateRangeBeforeAdd = (programId: string): { isValid: boolean; errors: { max_order_value_error?: string; discount_error?: string } } => {
    const currentProgram = program[programId];
    if (!currentProgram) return { isValid: true, errors: {} };
    
    const ranges = currentProgram.discount_ranges;
    if (ranges.length === 0) return { isValid: true, errors: {} };
    
    const errors: { max_order_value_error?: string; discount_error?: string, contract_error?: string } = {};
    const lastRangeIndex = ranges.length - 1;
    const lastRange = ranges[lastRangeIndex];
    
    // Check if discount is required
    if (!lastRange.discount) {
      errors.discount_error = 'Discount is required';
    }
    // Check if max value is required
    if (lastRange.max_order_value && lastRangeIndex !== ranges.length - 1) {
      errors.max_order_value_error = 'Max value is required';
    }
    
    // Check if max value is greater than previous max value
    if (ranges.length > 1) {
      const previousRange = ranges[lastRangeIndex - 1];
      if (lastRange.max_order_value && previousRange.max_order_value && 
          lastRange.max_order_value <= previousRange.max_order_value) {
        errors.max_order_value_error = errors.max_order_value_error 
          ? `${errors.max_order_value_error}, Max value must be greater than ${previousRange.max_order_value}`
          : `Max value must be greater than ${previousRange.max_order_value}`;
      }
    }
    
    return { isValid: Object.keys(errors).length === 0, errors };
  };

  const handleAddDiscountRange = (programId: string) => {
    const validation = validateRangeBeforeAdd(programId);
    
    if (!validation.isValid) {
      // Update program state with errors in the last range
      setProgram(prevState => {
        const currentProgram = prevState[programId];
        if (!currentProgram) return prevState;
        
        const updatedRanges = [...currentProgram.discount_ranges];
        const lastIndex = updatedRanges.length - 1;
        const lastRange = { ...updatedRanges[lastIndex] };
        
        if (validation.errors.max_order_value_error) {
          lastRange.max_order_value_error = validation.errors.max_order_value_error;
        }
        if (validation.errors.discount_error) {
          lastRange.discount_error = validation.errors.discount_error;
        }
        
        updatedRanges[lastIndex] = lastRange;
        
        return {
          ...prevState,
          [programId]: {
            ...currentProgram,
            discount_ranges: updatedRanges
          }
        };
      });
      
      // Scroll to the error
      const lastIndex = program[programId]?.discount_ranges.length - 1 || 0;
      scrollToFirstError(programId, lastIndex);
      return;
    }

    const currentProgram = program[programId];
    const lastRange = currentProgram?.discount_ranges?.[currentProgram.discount_ranges.length - 1];
    const newMinValue = lastRange ? (lastRange.max_order_value || 0) + 1 : 0;

    const newRange: DiscountRange = {
      min_order_value: newMinValue,
      max_order_value: null,
      discount: null,
      discount_error: '',
      max_order_value_error: ''
    };

    setProgram(prevState => ({
      ...prevState,
      [programId]: {
        ...prevState[programId],
        discount_ranges: [...(prevState[programId]?.discount_ranges || []), newRange]
      }
    }));
  };

  const handleRemoveDiscountRange = (programId: string, index: number) => {
    if (program[programId].discount_ranges.length <= 1) {
      setProgram(prevState => {
        const newState = { ...prevState };
        delete newState[programId];
        return newState;
      });
      return;
    }
    else {
      setProgram(prevState => ({
        ...prevState,
        [programId]: {
          ...prevState[programId],
          discount_ranges: prevState[programId].discount_ranges.filter((_, i) => i !== index)
        }
      }));
    }
  };

  const handleDiscountRangeChange = (programId: string, index: number, field: keyof DiscountRange, value: string | number) => {
    setProgram(prevState => {
      const currentProgram = prevState[programId];
      if (!currentProgram) return prevState;

      const updatedRanges = [...currentProgram.discount_ranges];
      const range = { ...updatedRanges[index] };
      
      // Convert value to number or null
      const numValue = value === '' ? null : Number(value);
      
      // Type-safe field assignment
      if (field === 'max_order_value') {
        range.max_order_value = numValue;
        range.max_order_value_error = '';
      } else if (field === 'discount') {
        range.discount = numValue;
        range.discount_error = '';
      } else if (field === 'min_order_value') {
        range.min_order_value = numValue || 0;
      }
      
      updatedRanges[index] = range;
      
      return {
        ...prevState,
        [programId]: {
          ...currentProgram,
          discount_ranges: updatedRanges
        }
      };
    });
  };

  const handleDateRangeSelect = (ranges: any) => {
    if (dateModalState) {
      const { programId } = dateModalState;
      const startDate = formatDateToLocalString(ranges.selection.startDate);
      const endDate = formatDateToLocalString(ranges.selection.endDate);
      
      setProgram(prevState => ({
        ...prevState,
        [programId]: {
          ...prevState[programId],
          contractStartDate: startDate,
          contractEndDate: endDate,
          contractError: ''
        }
      }));
      
      setDateModalState(null);
    }
  };

  const handleCheckboxChange = (e: React.ChangeEvent<HTMLInputElement>, program_details: Program) => {
    const isChecked = e.target.checked;
    const programId = program_details.programId;

    if (isChecked) {
      // Add program with default range
      const defaultRange: DiscountRange = {
        min_order_value: 0,
        max_order_value: null,
        discount: null,
        discount_error: '',
        max_order_value_error: ''
      };

      setProgram(prevState => ({
        ...prevState,
        [programId]: {
          checked: true,
          discount_ranges: [defaultRange],
          program_details: program_details,
          contractStartDate: prevState[programId]?.contractStartDate || '',
          contractEndDate: prevState[programId]?.contractEndDate || '',
          contractError: ''
        }
      }));
    } else {
      // Remove program
      setProgram(prevState => {
        const newState = { ...prevState };
        delete newState[programId];
        return newState;
      });
    }
  };

  const handleContinue = (e: React.MouseEvent<HTMLButtonElement>) => {
    e.preventDefault();
    
    // Validate all selected programs have valid discount ranges
    const validation = validateAllRanges();
    console.log(program,"currentProgram  inoiho");

    
    if (!validation.isValid && validation.firstError) {
              // Apply validation errors to the first invalid range
        const { programId, rangeIndex } = validation.firstError;
        const currentProgram = program[programId];
        if (currentProgram) {
          setProgram(prevState => {
            const updatedRanges = [...currentProgram.discount_ranges];
            const range = { ...updatedRanges[rangeIndex] };
            
            // Check if this is a contract date error (rangeIndex 0 and no contract dates)
            if (rangeIndex === 0 && (!currentProgram.contractStartDate || !currentProgram.contractEndDate)) {
              return {
                ...prevState,
                [programId]: {
                  ...currentProgram,
                  contractError: 'Contract date is required'
                }
              };
            }
            
            // Apply appropriate error messages for discount ranges
            if (!range.discount) {
              range.discount_error = 'Discount is required';
            }
            if (!range.max_order_value && rangeIndex !== currentProgram.discount_ranges.length - 1) {
              range.max_order_value_error = 'Max value is required';
            }
            
            // Check if max value is greater than previous max value
            if (rangeIndex > 0) {
              const previousRange = updatedRanges[rangeIndex - 1];
              if (range.max_order_value && previousRange.max_order_value && 
                  range.max_order_value <= previousRange.max_order_value) {
                range.max_order_value_error = `Max value must be greater than ${previousRange.max_order_value}`;
              }
            }
            
            updatedRanges[rangeIndex] = range;
            
            return {
              ...prevState,
              [programId]: {
                ...currentProgram,
                discount_ranges: updatedRanges
              }
            };
          });
          
          // Scroll to the first error
          scrollToFirstError(programId, rangeIndex);
        }
      
      return;
    }

    setShowReview(true);
  };

  if (showReview) {
    return (
      <ReviewPage
        {...localFormData}
        programIds={program}
        onBack={() => setShowReview(false)}
        onBackListing={onBackListing}
      />
    );
  }

  return (
    <div className="bg-white h-full">
      {/* Header */}
      <div className="flex border-b border-cardborder mb-8 flex-col md:flex-row md:items-center md:justify-between max-w-7xl mx-auto px-7 py-8">
        <div className="flex items-center gap-3">
          <button
            onClick={onBack}
            className="p-1.5 hover:bg-gray-100 rounded-full transition-colors"
            aria-label="Go back"
          >
            <ArrowLeft className="w-5 h-5 text-gray-600" />
          </button>
          <div>
            <h1 className="text-2xl font-bold text-gray-900">
              Build Catalogue
            </h1>
            <p className="text-sm text-gray-600 mt-1">
              Brands Included <strong>{Object.keys(program).length}</strong>
            </p>
          </div>
        </div>
      </div>

      {/* Content Section */}
      <div className="max-w-7xl mx-auto px-8 sm:px-6 lg:px-8">
        {/* Error State */}
        {programError && (
          <div className="bg-white rounded-xl p-6 flex flex-col justify-center min-h-[120px] transition-shadow duration-200 transition-colors border-[1px] border-cardborder mb-8">
            <div className="flex items-center gap-2 justify-center text-red-500">
              <AlertCircle className="w-5 h-5" />
              <span>{programError ? programError : "Something went wrong!"}</span>
            </div>
          </div>
        )}

        {/* Loading State */}
        {programLoading && (
          <div className="bg-white rounded-xl p-6 flex flex-col justify-center min-h-[120px] transition-shadow duration-200 transition-colors border-[1px] border-cardborder mb-8">
            <div className="flex justify-center items-center gap-2">
              <Loader2 className="w-10 h-10 animate-spin text-brand/80" />
              <span className="text-sm text-gray-500">Loading...</span>
            </div>
          </div>
        )}

        {/* Program List */}
        {!programLoading && !programError && (
          <div className="space-y-6">
            {programList?.map((item: any) => {
              const isChecked = program[item.programId]?.checked || false;
              return (
                <div key={item.programId} className="list_item bg-white rounded-xl p-6 flex flex-col justify-start transition-shadow duration-200 transition-colors border-[1px] border-cardborder">
                  {/* Program Header */}
                  <div className="flex items-center justify-between ">
                    <div className="flex items-center gap-3">
                      <img
                        src={item.logo}
                        alt={item.displayName}
                        className="w-20 h-12 object-contain  rounded-lg p-1"
                      />
                      <h3 className="text-lg font-semibold text-gray-900">
                        {item.displayName}
                      </h3>
                    </div>

                    {/* Toggle Switch */}
                    <label className="flex items-center cursor-pointer">
                      <input
                        type="checkbox"
                        checked={isChecked}
                        onChange={(e) => handleCheckboxChange(e, item)}
                        className="sr-only"
                      />
                      <div className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${isChecked ? 'bg-brand' : 'bg-gray-200'
                        }`}>
                        <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${isChecked ? 'translate-x-6' : 'translate-x-1'
                          }`} />
                      </div>
                    </label>
                  </div>

                  {/* Contract Date Range Section */}
                  {isChecked && (
                    <div className="mt-6">
                      <h4 className="text-sm ml-1 mb-2 font-medium text-gray-600">Contract Period</h4>
                      <div className="flex items-center gap-3 mb-4">
                        <button
                          type="button"
                          onClick={() => setDateModalState({ programId: item.programId })}
                          className="flex items-center gap-2 px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none"
                        >
                          <span>
                            {formatDateRange(
                              program[item.programId]?.contractStartDate || '',
                              program[item.programId]?.contractEndDate || ''
                            )}
                          </span>
                        </button>
                        {program[item.programId]?.contractError && (
                                  <span className="text-xs text-red-500">
                                    {program[item.programId]?.contractError}
                                  </span>
                                )}
                      </div>
                    </div>
                  )}

                  {/* Discount Ranges Section */}
                  {isChecked && (
                    <div >
                      <h4 className="text-sm mt-2 ml-1 mb-2 font-medium text-gray-600 ">Discount Ranges</h4>

                      <div className="border border-gray-300 rounded-lg p-4">
                        {/* Labels Row */}
                        <div className="flex items-center gap-3 mb-3">
                          <div className="w-48">
                            <label className="text-xs font-medium text-gray-700">Min Value</label>
                          </div>
                          <div className="w-4"></div> {/* Spacer for "to" text */}
                          <div className="w-48">
                            <label className="text-xs font-medium text-gray-700">Max Value</label>
                          </div>
                          <div className="w-48">
                            <label className="text-xs font-medium text-gray-700">Discount %</label>
                          </div>
                          <div className="w-8"></div> {/* Spacer for remove button */}
                        </div>

                        {program[item.programId]?.discount_ranges?.map((range, index) => (
                          <div 
                            key={index} 
                            className="flex flex-col gap-2 mb-3 last:mb-0"
                            data-program-id={item.programId}
                            data-range-index={index}
                          >
                            <div className="flex items-start gap-3 ">
                              {/* Min Value */}
                              <input
                                type="number"
                                value={range.min_order_value}
                                disabled={true}
                                placeholder="Min Value"
                                className="w-48 px-3 py-2 border border-gray-300 rounded-md bg-gray-100 text-sm text-gray-600 focus:outline-none"
                              />

                              <span className="text-sm text-gray-500 mt-2">to</span>

                              {/* Max Value */}
                              <div className="flex flex-col gap-1">
                                <input
                                  type="number"
                                  value={range.max_order_value || ''}
                                  onChange={(e) => handleDiscountRangeChange(item.programId, index, 'max_order_value', e.target.value)}
                              
                                  // placeholder="Max Value"
                                  className={`w-48 px-3 py-2 border rounded-md bg-white text-sm focus:outline-none ${range.max_order_value_error ? 'border-red-500' : 'border-gray-300'
                                    }`}
                                />
                                {range.max_order_value_error && (
                                  <span className="text-xs text-red-500">
                                    {range.max_order_value_error}
                                  </span>
                                )}
                              </div>

                              {/* Discount */}
                              <div className="flex flex-col gap-1">
                                <input
                                  type="number"
                                  value={range.discount || ''}
                                  onChange={(e) => handleDiscountRangeChange(item.programId, index, 'discount', e.target.value)}
                                  // placeholder="Discount %"
                                  className={`w-48 px-3 py-2 border rounded-md bg-white text-sm focus:outline-none ${range.discount_error ? 'border-red-500' : 'border-gray-300'}`}
                                />
                                {range.discount_error && (
                                  <span className="text-xs text-red-500">
                                    {range.discount_error}
                                  </span>
                                )}
                              </div>

                              {/* Remove Button */}
                              <button
                                type="button"
                                onClick={() => handleRemoveDiscountRange(item.programId, index)}
                                disabled={program[item.programId].discount_ranges[0].max_order_value === 0}
                                className="p-2 text-red-500 hover:text-red-700 hover:bg-gray-50 hover:rounded-full  disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                              >
                                <Trash2 className="w-4 h-4" />
                              </button>
                            </div>
                          </div>
                        ))}

                        {/* Add Range Button */}
                        <button
                          type="button"
                          onClick={() => handleAddDiscountRange(item.programId)}
                          disabled={false}
                          className="px-4 py-2 text-sm font-medium text-brand border border-brand rounded-md hover:bg-brand hover:text-white focus:outline-none transition-colors"
                        >
                          Add Range
                        </button>
                      </div>

                     
                    </div>
                  )}
                </div>
              );
            })}

            {/* Continue Button */}
            <div className="flex justify-between pb-8">
              <button
                type="button"
                onClick={onBack}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 focus:outline-none"
              >
                Back
              </button>

              <button
                type="button"
                onClick={handleContinue}
                className="px-4 py-2 text-sm font-medium text-white rounded-md bg-brand hover:bg-brand/80 focus:outline-none"
              >
                Continue
              </button>
            </div>
          </div>
        )}

        {/* Date Range Modal */}
        <CustomModal
          show={dateModalState !== null}
          onHide={() => setDateModalState(null)}
          title="Contract Period"
          width="w-[600px]"
        >
          <div className="flex flex-col items-center">
            <DateRangePicker
              ranges={[
                {
                  startDate: dateModalState ? new Date(program[dateModalState.programId]?.contractStartDate || new Date()) : new Date(),
                  endDate: dateModalState ? new Date(program[dateModalState.programId]?.contractEndDate || new Date()) : new Date(),
                  key: "selection",
                },
              ]}
              onChange={handleDateRangeSelect}
              minDate={new Date()}
            />
          </div>
        </CustomModal>
      </div>
    </div>
  );
};

export default BuildCataloguePage;